import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import DetailsBox from '../../../../../components/Cards/DetailsBox'

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 }
const SEVERITY_META = {
  critical: {
    label: 'Critical',
    color: 'error',
    Icon: ReportProblemRoundedIcon,
  },
  warning: {
    label: 'Warning',
    color: 'warning',
    Icon: WarningAmberRoundedIcon,
  },
  info: {
    label: 'Info',
    color: 'info',
    Icon: InfoRoundedIcon,
  },
}

const PlanningGapRow = ({ gap, onNavigateToTab }) => {
  const meta = SEVERITY_META[gap.severity] ?? SEVERITY_META.info
  const Icon = meta.Icon
  const showAction = onNavigateToTab && gap.anchor

  return (
    <Box
      sx={(theme) => {
        const paletteColor = theme.palette[meta.color]?.main || theme.palette.info.main
        return {
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(paletteColor, 0.18)}`,
          bgcolor: alpha(paletteColor, 0.055),
        }
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Stack direction="row" gap={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={(theme) => {
              const paletteColor = theme.palette[meta.color]?.main || theme.palette.info.main
              return {
                width: 38,
                height: 38,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                color: paletteColor,
                bgcolor: alpha(paletteColor, 0.11),
                flex: '0 0 auto',
              }
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" fontWeight={900}>
                {gap.section || 'Planning item'}
              </Typography>
              <Chip
                size="small"
                label={meta.label}
                color={meta.color}
                sx={{ height: 22, fontWeight: 800 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {gap.message}
            </Typography>
          </Box>
        </Stack>

        {showAction && (
          <Button
            size="small"
            color={meta.color}
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => onNavigateToTab(gap.anchor)}
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'center' },
              fontWeight: 800,
              whiteSpace: 'nowrap',
            }}
          >
            {gap.actionLabel ?? 'Review'}
          </Button>
        )}
      </Stack>
    </Box>
  )
}

const PlanningGapsList = ({ gaps = [], onNavigateToTab }) => {
  const sortedGaps = [...gaps].sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3),
  )

  return (
    <DetailsBox title="Planning gaps & next actions" hideTitle={false}>
      {sortedGaps.length === 0 ? (
        <Box
          sx={(theme) => ({
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.07),
            border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
          })}
        >
          <Stack direction="row" gap={1.5} alignItems="center">
            <CheckCircleRoundedIcon color="success" />
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              No planning gaps detected from the information currently entered.
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Stack gap={1.25}>
          {sortedGaps.map((gap, index) => (
            <PlanningGapRow
              key={`${gap.section}-${index}`}
              gap={gap}
              onNavigateToTab={onNavigateToTab}
            />
          ))}
        </Stack>
      )}
    </DetailsBox>
  )
}

export default PlanningGapsList
